import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { applyReflowStateAction, updateHiddenElementsAction } from 'editor/src/store/design/slice';
import removeAllSelectedMediaElementsOperation from 'editor/src/store/editor/operation/removeAllSelectedMediaElementsOperation';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import computeReflowState from './computeReflowState';
import getHiddenMediaElementUuids from './getHiddenMediaElementUuids';

/**
 * Sole entry point for recomputing derived spread state. Two producers feed
 * `element.hidden`: Conditions and Reflow rows. Reflow additionally mutates
 * `element.y` (collapsed rows stay at authored position; subsequent rows
 * shift up to overlap them) and records the applied shift on each row.
 *
 * The hide channel and the position channel are written in a single batch so
 * render sees a consistent state. NEVER dispatch `updateHiddenElementsAction`
 * or `applyReflowStateAction` directly from anywhere else — that would
 * silently un-hide every other producer's elements OR leave positions out of
 * sync with collapse state.
 */
const updateHiddenElementsOperation = () => (dispatch: ThunkDispatch, getState: () => RootState) => {
  const state = getState();
  const spreadIndex = getCurrentSpreadIndex(state);
  const spread = state.design.designData?.spreads[spreadIndex];
  if (!spread) {
    return;
  }

  const hasConditions = !!spread.conditionGroup;
  const hasReflowGroups = !!spread.reflowGroups?.length;
  if (!hasConditions && !hasReflowGroups) {
    return;
  }

  const conditionHidden = hasConditions ? getHiddenMediaElementUuids(spread.conditionGroup!) : new Set<number>();
  const reflow = computeReflowState(spread, conditionHidden);
  const hiddenElementIds = new Set<number>([...conditionHidden, ...reflow.hiddenUuids]);

  batch(() => {
    dispatch(
      updateHiddenElementsAction({
        spreadIndex,
        hiddenElementIds: [...hiddenElementIds],
      }),
    );
    if (reflow.elementUpdates.length || reflow.rowShiftUpdates.length) {
      dispatch(
        applyReflowStateAction({
          spreadIndex,
          elementUpdates: reflow.elementUpdates,
          rowShiftUpdates: reflow.rowShiftUpdates,
        }),
      );
    }
  });

  if (state.editor.selectedElementUuids.some((uuid) => hiddenElementIds.has(uuid))) {
    dispatch(removeAllSelectedMediaElementsOperation());
  }
};

export default updateHiddenElementsOperation;
